import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiAuthError } from './lib/api';
import { AppLayout } from './shell';
import type { BreadcrumbItem, SidebarSection, SidebarUser } from './shell';
import { useToast, ImpersonationBanner, ViewAsMenu, CommandBar, NotificationBell } from './organisms';
import { Button, SearchTrigger, IconSignOut } from './atoms';
import { identify, groupTenant, resetAnalytics } from './lib/analytics';
import { useBreadcrumbs } from './lib/useBreadcrumbs';
import { usePermissions } from './lib/usePermissions';
import { visibleSections } from './lib/navGating';
import { useFlag, FEATURE } from './lib/useFlag';
import type { GatedSection } from './lib/navGating';
import { setActiveOrgTz } from './lib/orgTimezone';
import { ClientPortalApp } from './portal/ClientPortalApp';
import { ConsultantPortalApp } from './portal/ConsultantPortalApp';
import { BrandingProvider, useBranding } from './lib/useBranding';
import type { ReactNode } from 'react';

/**
 * App — root authenticated-layout wrapper.
 *
 * Renders Sidebar + TopBar via AppLayout and an <Outlet /> for the
 * matched child route. Child routes mount inside that outlet — Dashboard
 * (index), future Clients / Properties / Parcels list+detail screens, etc.
 *
 * Auth gating: on 401 from /api/current-user, navigates to /login with
 * a `next` query parameter so the operator returns to where they were
 * trying to go after logging in. (basename="/" — the navigate target is
 * root-relative; FR-6 cutover retired the old /v2 base.)
 *
 * Phase 11 Block 5/6 foundation + Block 7 PR 1 (Outlet + nested routes).
 */

interface CurrentUserResponse {
  id: string;
  email: string;
  role: string;
  organization_id: string | null;
  first_name: string | null;
  last_name: string | null;
  employee_title: string | null;
  org_name: string | null;
  timezone?: string | null;
  // Impersonation state (super_admin "View as"). Present only while this session
  // was rebound to another user by a super_admin; `actor` names the real actor.
  impersonating?: boolean;
  actor?: { id: string; name: string; email: string; role: string } | null;
}

// Roles that may use the "View as" control (mount only for these).
const CAN_VIEW_AS = new Set(['super_admin', 'admin']);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const currentUserQuery = useQuery<CurrentUserResponse>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUserResponse>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  // Name-resolved breadcrumb trail. Called unconditionally (before any early
  // return) so its internal queries obey the rules of hooks.
  const breadcrumbs = useBreadcrumbs(location.pathname);

  // The caller's own permission grid — drives which object nav items render.
  // Called unconditionally (rules of hooks) before the portal early-returns; the
  // portal shells simply don't consume it. Default-DENY: while it loads, gated
  // items stay hidden.
  const permissionsQuery = usePermissions();
  const permissionMap = permissionsQuery.data?.permissions;

  useEffect(() => {
    if (currentUserQuery.error instanceof ApiAuthError) {
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [currentUserQuery.error, navigate, location.pathname]);

  // Analytics identity — person = app user id; tenant = org group. Fail-open,
  // no-op when analytics is unkeyed. NO PII in props (email/name omitted); the
  // org name lives on the GROUP only. Runs once per authenticated user load.
  const authedUser = currentUserQuery.data;
  // Publish the org timezone to the module cache the pure date formatters read,
  // so formatDate/formatDateTime render in the org zone everywhere — including
  // column-cell renderers and other non-hook contexts (date-consistency).
  useEffect(() => {
    setActiveOrgTz(authedUser?.timezone ?? undefined);
  }, [authedUser?.timezone]);
  useEffect(() => {
    if (!authedUser?.id) return;
    identify(authedUser.id, { role: authedUser.role });
    if (authedUser.organization_id) {
      groupTenant(authedUser.organization_id, authedUser.org_name ?? undefined);
    }
  }, [authedUser?.id, authedUser?.role, authedUser?.organization_id, authedUser?.org_name]);

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
      resetAnalytics(); // clear identity so the next user isn't merged. Fail-open.
      // Route to the login surface FIRST, then drop cached state: navigating
      // unmounts the authed tree, so clearing can't briefly re-render App with a
      // stale-but-emptied user (which for a client would flash the operator
      // shell / leak staff nav). clear() evicts every client-scoped query so no
      // portal data survives into the next session — the portal login → portal →
      // logout → login round-trip lands on a clean slate.
      navigate('/login', { replace: true });
      queryClient.clear();
      toast({ variant: 'info', message: 'Signed out.' });
    } catch {
      toast({ variant: 'error', message: 'Logout failed' });
    }
  }

  // ⚠️ EVERY HOOK ABOVE EVERY CONDITIONAL RETURN — including the signed-out one below.
  //
  // This hook has been hoisted TWICE, and the second time is the lesson. It first sat below
  // the client/consultant role router, which white-screened every portal user. That fix moved
  // it above the role router — but NOT above the `ApiAuthError` return immediately below, so
  // the identical violation survived on the SIGNED-OUT path: render 1 the query is still
  // loading so we fall through and this hook RUNS; render 2 it rejects, we return null, and it
  // does NOT. React sees fewer hooks, throws #300, and the boundary white-screens a signed-out
  // deep link. Reported against /parcels/new — but the crash is in this shell, not the wizard.
  //
  // Hoisting a hook to just above the return that bit you is not the fix. The rule is
  // absolute: no hook below ANY conditional return.
  const buildModeOn = useFlag(FEATURE.BUILD_MODE);

  if (currentUserQuery.error instanceof ApiAuthError) {
    return null;
  }

  const user = currentUserQuery.data;

  // ─── Impersonation banner (super_admin "View as") ─────────
  // When this session was rebound to another user by a super_admin, a persistent
  // banner rides above EVERY shell (operator, client portal, consultant) with a
  // "return to self" control. Rendered as a fragment sibling so it survives the
  // role router below regardless of which shell renders.
  const currentName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
    : '';
  const impersonationBanner = user?.impersonating && user.actor ? (
    <ImpersonationBanner
      viewingName={currentName}
      viewingRole={user.role}
      actorName={user.actor.name}
    />
  ) : null;
  const withBanner = (content: ReactNode) => (
    <>
      {impersonationBanner}
      {content}
    </>
  );

  // ─── Role router (WS-3.2) ─────────────────────────────────
  // External roles get their own focused portal shell instead of the operator
  // app. Operators (super_admin/admin/administrator/analyst/agent) keep the full
  // AppLayout + Outlet below. While the user is still loading, `role` is undefined
  // and we fall through to the operator render (which tolerates an undefined user).
  const portalUser = user ? {
    name: currentName,
    email: user.email,
    org_name: user.org_name,
  } : undefined;
  if (user?.role === 'client') return withBanner(<ClientPortalApp user={portalUser} onLogout={handleLogout} />);
  // Consultant model decision is still pending — gate defensively: this branch
  // only renders when the backend actually issues role==='consultant' at runtime;
  // otherwise it is dead code and the operator app below is unaffected.
  if (user?.role === 'consultant') return withBanner(<ConsultantPortalApp user={portalUser} onLogout={handleLogout} />);

  const sidebarUser = user ? {
    name: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email,
    email: user.email,
    role: user.role,
  } : undefined;

  // Each browsable object item declares a `gate` (entity `can_view`, or
  // super_admin-only); aggregate/workflow surfaces (Dashboard, Calendar, Appeals
  // queue, Year Roll) are ungated. The three fee/deal surfaces gate on their own
  // entity — hidden by default (the demo/default seed withholds the grant from
  // non-super roles) yet revealed the moment a super_admin grants it. Org Settings,
  // Audit Log, and User Management are super_admin-only. visibleSections() drops
  // any section left empty after gating. Default-DENY while the grid loads.
  const gatedSections: GatedSection[] = [
    // Ignite's own surfaces. Every entry points at a route that exists — the nav
    // is not a wish list, and an item that navigates nowhere teaches people the
    // app is broken. Journey surfaces (Assess, Connect, Develop, Validate,
    // Procure) join this list as their slices land; see
    // specs/0001-supplier-journey/design.md for the build order.
    {
      label: 'Workqueue',
      items: [
        { to: '/', label: 'Dashboard', end: true },
        { to: '/calendar', label: 'Calendar' },
      ],
    },
    {
      // Build Mode® — off by default; the flag decides whether the surface
      // exists here at all, superAdmin decides who may use it once it does.
      items: buildModeOn
        ? [{ to: '/build', label: 'Build Mode®', gate: { superAdmin: true } }]
        : [],
    },
    {
      label: 'Registry',
      items: [
        { to: '/clients/new', label: '+ New business', gate: { entity: 'client', action: 'can_add' } },
        { to: '/clients', label: 'Businesses', end: true, gate: { entity: 'client' } },
        { to: '/parent-clients', label: 'Parent organizations', gate: { entity: 'parent_client' } },
        { to: '/contacts', label: 'People', gate: { entity: 'contact' } },
      ],
    },
    {
      // The Supplier Journey, in the order it is walked. Each entry is one record
      // type on the generic CRUD surface; the relationships between them are
      // ordinary foreign keys, so a referral opens its business, its capability
      // and its partner without bespoke pages.
      label: 'Journey',
      items: [
        { to: '/assessments', label: 'Assessments', end: true, gate: { entity: 'assessment' } },
        { to: '/assessment-templates', label: 'Instruments', gate: { entity: 'assessment' } },
        { to: '/referrals', label: 'Referrals', gate: { entity: 'referral' } },
        { to: '/partners', label: 'Partners', gate: { entity: 'partner' } },
        { to: '/capabilities', label: 'Capabilities', gate: { entity: 'capability' } },
        { to: '/programs', label: 'Programs', gate: { entity: 'program' } },
        { to: '/cohorts', label: 'Cohorts', gate: { entity: 'program' } },
        { to: '/enrollments', label: 'Enrollments', gate: { entity: 'enrollment' } },
        { to: '/program-sessions', label: 'Sessions', gate: { entity: 'program_session' } },
        { to: '/attendance', label: 'Attendance', gate: { entity: 'program_session' } },
        // Learning sits at the foot of Journey: curriculum is what closes the
        // gaps Assess measures, so it belongs with the journey rather than under
        // System with the admin surfaces.
        { to: '/learning', label: 'Learning', gate: { entity: 'course' } },
        { to: '/courses', label: 'Courses', gate: { entity: 'course' } },
      ],
    },

    {
      label: 'Casework',
      items: [
        // `end` so /documents matches EXACTLY — otherwise it prefix-matches the
        // review queue and stays highlighted while you are on it, making that
        // item's click look like it did not take.
        { to: '/documents', label: 'Documents', end: true, gate: { entity: 'document' } },
        { to: '/documents/review-queue', label: 'Document review', gate: { entity: 'document' } },
        { to: '/information-requests', label: 'Information requests', gate: { entity: 'information_request' } },
        { to: '/notifications', label: 'Notifications', gate: { entity: 'notification' } },
        { to: '/activity-log', label: 'Activity log' },
      ],
    },
    {
      label: 'Billing',
      items: [
        { to: '/invoices', label: 'Invoices', gate: { entity: 'invoice' } },
        { to: '/invoice-items', label: 'Invoice items', gate: { entity: 'invoice_item' } },
        { to: '/invoice-terms', label: 'Invoice terms', gate: { entity: 'invoice_term' } },
        { to: '/quickbooks', label: 'QuickBooks sync', gate: { entity: 'invoice' } },
      ],
    },
    {
      label: 'System',
      items: [
        { to: '/user-management', label: 'Users & roles', gate: { superAdmin: true } },
        { to: '/org-settings', label: 'Settings', gate: { superAdmin: true } },
        { to: '/custom-fields', label: 'Custom fields', gate: { entity: 'custom_field' } },
        { to: '/forms', label: 'Forms' },
        { to: '/imports', label: 'Import', gate: { entity: 'import_batch' } },
        { to: '/audit-logs', label: 'Audit log', gate: { superAdmin: true } },
      ],
    },
    {
      label: 'Reports',
      items: [
        { to: '/reports', label: 'All reports', end: true, gate: { entity: 'report' } },
        { to: '/report-builder', label: 'Report builder', gate: { entity: 'report' } },
      ],
    },
  ];

  // Pass isLoading so gated items show optimistically until the permission grid
  // resolves — a stable nav item set from first paint, so nothing shifts under the
  // cursor mid-click (the "some nav items need a double-click" report). The
  // RouteGuard still enforces the real grant on click.
  const sidebarSections: SidebarSection[] = visibleSections(
    gatedSections,
    permissionMap,
    user?.role,
    permissionsQuery.isLoading,
  );

  // "View as" control — top-bar picker, mounted only for super_admin/admin.
  const viewAsControl = user && CAN_VIEW_AS.has(user.role)
    ? <ViewAsMenu currentUserId={user.id} />
    : null;

  // Branding provider wraps the operator shell so an admin's white-label edits
  // (primary/accent colors + display name) apply app-wide via the dedicated
  // /api/branding surface. Non-blocking: children always render (defaults on error).
  return withBanner(
    <BrandingProvider>
      <OperatorLayout
        sections={sidebarSections}
        sidebarUser={sidebarUser}
        breadcrumbs={breadcrumbs}
        fallbackBrandName={user?.org_name ?? 'Ignite'}
        onLogout={handleLogout}
        viewAsControl={viewAsControl}
      />
    </BrandingProvider>
  );
}

/**
 * The operator AppLayout, themed by the org's branding. Reads useBranding() (so
 * it must render INSIDE <BrandingProvider>): the sidebar brand name prefers the
 * org's white-label display_name, falling back to the org name / 'Ignite'.
 */
function OperatorLayout({
  sections,
  sidebarUser,
  breadcrumbs,
  fallbackBrandName,
  onLogout,
  viewAsControl,
}: {
  sections: SidebarSection[];
  sidebarUser: SidebarUser | undefined;
  breadcrumbs: BreadcrumbItem[];
  fallbackBrandName: string;
  onLogout: () => void;
  viewAsControl?: ReactNode;
}): ReactNode {
  const branding = useBranding();
  const brandName = branding.display_name && branding.display_name !== 'Ignite'
    ? branding.display_name
    : fallbackBrandName;

  return (
    <AppLayout
      sidebar={{
        sections,
        user: sidebarUser,
        brandName,
        brandTagline: 'v2',
      }}
      topbar={{
        breadcrumbs,
        actions: (
          <>
            <SearchTrigger />
            <NotificationBell />
            {viewAsControl}
            <Button variant="ghost" size="sm" onClick={onLogout} aria-label="Sign out" title="Sign out">
              <IconSignOut size={18} />
            </Button>
          </>
        ),
      }}
    >
      {/* Global ⌘K command-bar search — mounted once for the operator shell. */}
      <CommandBar />
      <Outlet />
    </AppLayout>
  );
}

export default App;
